import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import { useWindowSize } from '@react-hook/window-size';
import { jsPDF } from 'jspdf';
import { FaSignOutAlt } from 'react-icons/fa';
import { API_CONFIG } from '../../../utils/apiConfig';
import { useAuth } from '../../../contexts/AuthContext';
import './DigitalMarketingAdminPanel.css';

const DigitalMarketingAdminPanel = () => {
  const { userProfile, currentUser, loading, authInitialized, isRestoring, logout } = useAuth();
  const [showPettyVouchers, setShowPettyVouchers] = useState(true);
  const [pettyVouchers, setPettyVouchers] = useState([]);
  const [pettyCashVouchers, setPettyCashVouchers] = useState([]);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState('');
  const [confettiActive, setConfettiActive] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Unified loading state for create/save
  const [newVoucher, setNewVoucher] = useState({
    date: '',
    amount: '',
    purpose: '',
    cashStatus: 'pending',
    paidTo: '',
    receivedBy: '',
    paidBy: '',
    account: '',
    voucherType: 'pettyCashVoucher',
  });

  // Check if user is Super Admin
  const isSuperAdmin = userProfile?.role === 'super_admin';

  // Function to send edit request to Super Admin
  const sendEditRequest = async (voucher, voucherType) => {
    try {
      const requestData = {
        billType: voucherType === 'pettyVoucher' ? 'pettyVoucher' : 'pettyCashVoucher',
        billId: voucher._id || voucher.id,
        requestedBy: userProfile?.email || 'Unknown',
        reason: `Request to edit ${voucherType === 'pettyVoucher' ? 'Petty' : 'Petty Cash'} Voucher: ${voucher.voucherId}`,
        status: 'pending',
        voucherData: voucher,
        createdAt: new Date().toISOString()
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser?.getIdToken()}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        setShowSuccess('Edit request sent to Super Admin for approval');
        setTimeout(() => setShowSuccess(''), 3000);
      } else {
        setError('Failed to send edit request');
      }
    } catch (err) {
      setError('Failed to send edit request');
    }
  };

  // Function to send delete request to Super Admin
  const sendDeleteRequest = async (voucher, voucherType) => {
    try {
      const requestData = {
        billType: voucherType === 'pettyVoucher' ? 'pettyVoucher' : 'pettyCashVoucher',
        billId: voucher._id || voucher.id,
        requestedBy: userProfile?.email || 'Unknown',
        reason: `Request to delete ${voucherType === 'pettyVoucher' ? 'Petty' : 'Petty Cash'} Voucher: ${voucher.voucherId}`,
        status: 'pending',
        voucherData: voucher,
        action: 'delete',
        createdAt: new Date().toISOString()
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser?.getIdToken()}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        setShowSuccess('Delete request sent to Super Admin for approval');
        setTimeout(() => setShowSuccess(''), 3000);
      } else {
        setError('Failed to send delete request');
      }
    } catch (err) {
      setError('Failed to send delete request');
    }
  };

  // Function to handle direct delete for Super Admin
  const handleDeleteVoucher = async (voucherId) => {
    if (!window.confirm('Are you sure you want to delete this petty voucher?')) {
      return;
    }

    try {
      setIsLoading(true);
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/petty-vouchers/${voucherId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.ok) {
        setShowSuccess('Petty voucher deleted successfully');
        // Remove from state
        setPettyVouchers(prev => prev.filter(v => v._id !== voucherId));
        // Update localStorage
        const updatedVouchers = pettyVouchers.filter(v => v._id !== voucherId);
        saveLocalData('digitalMarketingPettyVouchers', updatedVouchers);
        setTimeout(() => setShowSuccess(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to delete petty voucher');
      }
    } catch (err) {
      setError('Failed to delete petty voucher');
    } finally {
      setIsLoading(false);
    }
  };
  const [currentDate, setCurrentDate] = useState('');
  const [pettyVoucherFilterDate, setPettyVoucherFilterDate] = useState('');
  const [pettyCashVoucherFilterDate, setPettyCashVoucherFilterDate] = useState('');
  const [width, height] = useWindowSize();
  const navigate = useNavigate();

  const API_BASE_URL = `${API_CONFIG.BASE_URL}/api/digital-marketing`;

  // Update voucher type based on current section
  useEffect(() => {
    setNewVoucher(prev => ({
      ...prev,
      voucherType: showPettyVouchers ? 'pettyVoucher' : 'pettyCashVoucher'
    }));
  }, [showPettyVouchers]);

  // Local storage functions for offline functionality
  const loadLocalData = (key) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  };

  const saveLocalData = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
    }
  };

  useEffect(() => {
    const today = new Date();
    setCurrentDate(today.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }));

    // Check if user is authenticated using Firebase Auth
    // Only redirect if auth is initialized, not restoring, and no user is found
    if (authInitialized && !isRestoring && !currentUser) {
      setError('Please log in to access the admin panel');
      navigate('/login');
      return;
    }

    // If still loading, auth not initialized, or still restoring, don't proceed with data fetching
    if (loading || !authInitialized || isRestoring) {
      return;
    }

    // Load local data first
    const localPettyVouchers = loadLocalData('digitalMarketingPettyVouchers');
    const localPettyCashVouchers = loadLocalData('digitalMarketingPettyCashVouchers');
    
    if (localPettyVouchers.length > 0) {
      setPettyVouchers(localPettyVouchers);
    }
    if (localPettyCashVouchers.length > 0) {
      setPettyCashVouchers(localPettyCashVouchers);
    }

    const fetchData = async (url, setData) => {
      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        
        if (response.status === 404 || response.status === 400) {
          setData([]);
          setError('API endpoint not available. Using local data. Records will sync when server is available.');
          setTimeout(() => setError(''), 5000);
          return;
        }
        
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${response.status}: Failed to fetch data`);
        }
        
        const data = await response.json();
        
        // Check if any vouchers have empty additional fields
        const vouchersWithEmptyFields = data.filter(v => 
          !v.paidTo && !v.account && !v.receivedBy && !v.paidBy
        );
        if (vouchersWithEmptyFields.length > 0) {
        }
        
        setData(data);
        setError('');
        
        // Save to localStorage for offline functionality
        const storageKey = url.includes('petty-vouchers') ? 'digitalMarketingPettyVouchers' : 'digitalMarketingPettyCashVouchers';
        saveLocalData(storageKey, data);
      } catch (err) {
        if (err.message.includes('404') || err.message.includes('400') || err.message.includes('Not Found')) {
          setData([]);
          setError('API endpoint not available. Using local data. Records will sync when server is available.');
          setTimeout(() => setError(''), 5000);
        } else if (err.message.includes('Failed to fetch') || err.message.includes('Network Error')) {
          setData([]);
          // Don't show error for network issues, just use empty data
          setError('');
        } else {
          setError(`Failed to fetch vouchers: ${err.message}`);
        }
      }
    };

    fetchData(`${API_BASE_URL}/petty-vouchers`, setPettyVouchers);
    fetchData(`${API_BASE_URL}/petty-cash-vouchers`, setPettyCashVouchers);
  }, [navigate, API_BASE_URL, currentUser, loading, authInitialized, isRestoring]);

  const handleCreateVoucher = async () => {
    setIsLoading(true);
    setError(''); // Clear any previous errors
    try {
      if (!newVoucher.date || !newVoucher.amount || !newVoucher.purpose) {
        setError('Date, amount, and purpose are required');
        setIsLoading(false);
        return;
      }
      const amount = parseFloat(newVoucher.amount);
      if (isNaN(amount) || amount <= 0) {
        setError('Amount must be a positive number');
        setIsLoading(false);
        return;
      }

      if (!loading && !currentUser) {
        setError('Please log in to create a voucher');
        navigate('/login');
        setIsLoading(false);
        return;
      }

      if (loading) {
        setError('Please wait while authentication is being verified...');
        setIsLoading(false);
        return;
      }

      const isPettyVoucher = newVoucher.voucherType === 'pettyVoucher';
      const url = isPettyVoucher ? `${API_BASE_URL}/petty-vouchers` : `${API_BASE_URL}/petty-cash-vouchers`;

      const payload = isPettyVoucher
        ? {
            date: new Date(newVoucher.date).toISOString(),
            amount: amount,
            purpose: newVoucher.purpose,
            paidTo: newVoucher.paidTo || null,
            account: newVoucher.account || null,
            receivedBy: newVoucher.receivedBy || null,
            paidBy: newVoucher.paidBy || null,
          }
        : {
            date: new Date(newVoucher.date).toISOString(),
            amount: amount,
            purpose: newVoucher.purpose,
            cashStatus: newVoucher.cashStatus,
            relatedPettyVoucherId: newVoucher.relatedPettyVoucherId || undefined,
            paidTo: newVoucher.paidTo || null,
            account: newVoucher.account || null,
            receivedBy: newVoucher.receivedBy || null,
            paidBy: newVoucher.paidBy || null,
          };

      const idToken = await currentUser.getIdToken();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 404 || response.status === 400) {
        // Create local record as fallback
        const localRecord = {
          _id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...payload,
          createdAt: new Date().toISOString(),
          isLocal: true
        };
        
        if (isPettyVoucher) {
          const updatedVouchers = [...pettyVouchers, localRecord];
          setPettyVouchers(updatedVouchers);
          saveLocalData('digitalMarketingPettyVouchers', updatedVouchers);
        } else {
          const updatedCashVouchers = [...pettyCashVouchers, localRecord];
          setPettyCashVouchers(updatedCashVouchers);
          saveLocalData('digitalMarketingPettyCashVouchers', updatedCashVouchers);
        }
        
        setError('Voucher created locally (API endpoint unavailable). Data will sync when server is available.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to create voucher`);
      }

      const result = await response.json();

      setShowSuccess('Voucher Created!');
      setConfettiActive(true);
      
      if (isPettyVoucher) {
        const updatedVouchers = [...pettyVouchers, result];
        setPettyVouchers(updatedVouchers);
        saveLocalData('digitalMarketingPettyVouchers', updatedVouchers);
      } else {
        const updatedCashVouchers = [...pettyCashVouchers, result];
        setPettyCashVouchers(updatedCashVouchers);
        saveLocalData('digitalMarketingPettyCashVouchers', updatedCashVouchers);
      }
      
      setNewVoucher({
        date: '',
        amount: '',
        purpose: '',
        cashStatus: 'pending',
        paidTo: '',
        receivedBy: '',
        paidBy: '',
        account: '',
        voucherType: newVoucher.voucherType, // Preserve current voucher type
        relatedPettyVoucherId: '',
      });

      const fetchData = async (url, setData) => {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error);
        setData(data);
      };

      if (isPettyVoucher) {
        fetchData(`${API_BASE_URL}/petty-vouchers`, setPettyVouchers);
      } else {
        fetchData(`${API_BASE_URL}/petty-cash-vouchers`, setPettyCashVouchers);
      }

      setTimeout(() => {
        setShowSuccess('');
        setConfettiActive(false);
      }, 2000);
    } catch (err) {
      setError(`Failed to create voucher: ${err.message}`);
      if (err.message.includes('token') || err.message.includes('unauthorized')) {
        setError('Authentication failed. Please refresh the page.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditVoucher = (voucher, isPettyVoucher) => {
    setEditingVoucher({
      ...voucher,
      isPettyVoucher,
      date: new Date(voucher.date).toISOString().split('T')[0], // Ensure correct date format
      paidTo: voucher.paidTo || '',
      receivedBy: voucher.receivedBy || '',
      paidBy: voucher.paidBy || '',
      account: voucher.account || '',
      relatedPettyVoucherId: voucher.relatedPettyVoucherId || '',
    });
  };

  const handleSaveVoucher = async () => {
    setIsLoading(true);
    try {
      if (!editingVoucher.date || !editingVoucher.amount || !editingVoucher.purpose) {
        setError('Date, amount, and purpose are required');
        return;
      }
      if (parseFloat(editingVoucher.amount) <= 0) {
        setError('Amount must be positive');
        return;
      }

      if (!loading && !currentUser) {
        setError('Please log in to update a voucher');
        navigate('/login');
        return;
      }

      if (loading) {
        setError('Please wait while authentication is being verified...');
        return;
      }

      // Verify voucher exists
      const isPettyVoucher = editingVoucher.isPettyVoucher;
      const voucherList = isPettyVoucher ? pettyVouchers : pettyCashVouchers;
      const voucherExists = voucherList.some((v) => v._id === editingVoucher._id);
      if (!voucherExists) {
        setError('Voucher not found. Please refresh and try again.');
        return;
      }

      const url = isPettyVoucher
        ? `${API_BASE_URL}/petty-vouchers/${editingVoucher._id}`
        : `${API_BASE_URL}/petty-cash-vouchers/${editingVoucher._id}`;

      const payload = isPettyVoucher
        ? {
            voucherId: editingVoucher.voucherId || `VCH${Date.now()}`, // Fallback voucherId
            date: new Date(editingVoucher.date).toISOString(),
            amount: parseFloat(editingVoucher.amount),
            purpose: editingVoucher.purpose,
          }
        : {
            voucherId: editingVoucher.voucherId || `CASH${Date.now()}`,
            date: new Date(editingVoucher.date).toISOString(),
            amount: parseFloat(editingVoucher.amount),
            purpose: editingVoucher.purpose,
            cashStatus: editingVoucher.cashStatus,
            relatedPettyVoucherId: editingVoucher.relatedPettyVoucherId || undefined,
          };

      const idToken = await currentUser.getIdToken();

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 404 || response.status === 400) {
        // Update local record as fallback
        const updatedRecord = {
          ...editingVoucher,
          ...payload,
          updatedAt: new Date().toISOString(),
          isLocal: true
        };
        
        if (isPettyVoucher) {
          const updatedVouchers = pettyVouchers.map(v => v._id === editingVoucher._id ? updatedRecord : v);
          setPettyVouchers(updatedVouchers);
          saveLocalData('digitalMarketingPettyVouchers', updatedVouchers);
        } else {
          const updatedCashVouchers = pettyCashVouchers.map(v => v._id === editingVoucher._id ? updatedRecord : v);
          setPettyCashVouchers(updatedCashVouchers);
          saveLocalData('digitalMarketingPettyCashVouchers', updatedCashVouchers);
        }
        
        setError('Voucher updated locally (API endpoint unavailable). Data will sync when server is available.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to update voucher`);
      }

      await response.json();

      // Refetch data to ensure state consistency
      const fetchData = async (url, setData) => {
        try {
          const idToken = await currentUser.getIdToken();
          const response = await fetch(url, { headers: { Authorization: `Bearer ${idToken}` } });
          
          if (response.status === 404 || response.status === 400) {
            return;
          }
          
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || `HTTP ${response.status}: Failed to refetch data`);
          }
          
          const data = await response.json();
          setData(data);
        } catch (err) {
        }
      };

      if (isPettyVoucher) {
        fetchData(`${API_BASE_URL}/petty-vouchers`, setPettyVouchers);
      } else {
        fetchData(`${API_BASE_URL}/petty-cash-vouchers`, setPettyCashVouchers);
      }

      setEditingVoucher(null);
      setShowSuccess('Voucher Updated!');
      setConfettiActive(true);
      setTimeout(() => {
        setShowSuccess('');
        setConfettiActive(false);
      }, 2000);
    } catch (err) {
      setError(`Failed to update voucher: ${err.message}`);
      if (err.message.includes('token') || err.message.includes('Access denied')) {
        setError('Authentication failed. Please refresh the page.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCashVoucher = async (id) => {
    setIsLoading(true);
    try {
      if (!loading && !currentUser) {
        setError('Please log in to delete a voucher');
        navigate('/login');
        return;
      }

      if (loading) {
        setError('Please wait while authentication is being verified...');
        return;
      }

      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/petty-cash-vouchers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (response.status === 404 || response.status === 400) {
        // Delete from local state as fallback
        const updatedCashVouchers = pettyCashVouchers.filter((v) => v._id !== id);
        setPettyCashVouchers(updatedCashVouchers);
        saveLocalData('digitalMarketingPettyCashVouchers', updatedCashVouchers);
        setError('Voucher deleted locally (API endpoint unavailable). Data will sync when server is available.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to delete voucher`);
      }

      const updatedCashVouchers = pettyCashVouchers.filter((v) => v._id !== id);
      setPettyCashVouchers(updatedCashVouchers);
      saveLocalData('digitalMarketingPettyCashVouchers', updatedCashVouchers);
      setShowSuccess('Cash Voucher Deleted!');
      setConfettiActive(true);
      setTimeout(() => {
        setShowSuccess('');
        setConfettiActive(false);
      }, 2000);
    } catch (err) {
      setError(`Failed to delete voucher: ${err.message}`);
      if (err.message.includes('token')) {
        setError('Authentication failed. Please refresh the page.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = (isPettyVoucher) => {
    const vouchersToPrint = isPettyVoucher
      ? filterVouchersByDate(pettyVouchers, pettyVoucherFilterDate)
      : filterVouchersByDate(pettyCashVouchers, pettyCashVoucherFilterDate);

    if (!vouchersToPrint || vouchersToPrint.length === 0) {
      setError('No vouchers available to print');
      return;
    }

    try {
      let printContent;
      if (isPettyVoucher) {
        printContent = `
          <table class="print-table">
            <thead>
              <tr>
                <th style="width: 12.5%;">VOUCHER ID</th>
                <th style="width: 12.5%;">DATE</th>
                <th style="width: 12.5%;">AMOUNT</th>
                <th style="width: 12.5%;">PURPOSE</th>
                <th style="width: 12.5%;">PAID TO</th>
                <th style="width: 12.5%;">ACCOUNT</th>
                <th style="width: 12.5%;">RECEIVED BY</th>
                <th style="width: 12.5%;">PAID BY</th>
              </tr>
            </thead>
            <tbody>
              ${vouchersToPrint
                .map(
                  (v) => `
                <tr>
                  <td>${v.voucherId || '-'}</td>
                  <td>${currentDate}</td>
                  <td>₹${v.amount.toFixed(2)}</td>
                  <td>${v.purpose || '-'}</td>
                  <td>${v.paidTo || '-'}</td>
                  <td>${v.account || '-'}</td>
                  <td>${v.receivedBy || '-'}</td>
                  <td>${v.paidBy || '-'}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        `;
      } else {
        printContent = `
          <div class="print-container">
            <h1 class="print-title">PETTY CASH VOCHERS</h1>
            <table class="print-table">
              <thead>
                <tr class="table-header">
                  <th style="width: 10%;">VOUCHER ID</th>
                  <th style="width: 10%;">DATE</th>
                  <th style="width: 10%;">AMOUNT</th>
                  <th style="width: 10%;">PURPOSE</th>
                  <th style="width: 10%;">PAID TO</th>
                  <th style="width: 10%;">ACCOUNT</th>
                  <th style="width: 10%;">RECEIVED BY</th>
                  <th style="width: 10%;">PAID BY</th>
                  <th style="width: 10%;">STATUS</th>
                  <th style="width: 10%;">RELATED PETTY VOUCHER</th>
                </tr>
              </thead>
              <tbody>
                ${vouchersToPrint
                  .map(
                    (v) => `
                  <tr>
                    <td>${v.voucherId || '-'}</td>
                    <td>${v.date ? new Date(v.date).toLocaleDateString('en-GB') : currentDate}</td>
                    <td>₹${(v.amount || 0).toFixed(2)}</td>
                    <td>${v.purpose || '-'}</td>
                    <td>${v.paidTo || '-'}</td>
                    <td>${v.account || '-'}</td>
                    <td>${v.receivedBy || '-'}</td>
                    <td>${v.paidBy || '-'}</td>
                    <td>${v.cashStatus || '-'}</td>
                    <td>${
                      v.relatedPettyVoucherId
                        ? (() => {
                            const relatedVoucher = pettyVouchers.find((pv) => (pv._id === v.relatedPettyVoucherId || pv.id === v.relatedPettyVoucherId));
                            return relatedVoucher ? relatedVoucher.voucherId : '-';
                          })()
                        : '-'
                    }</td>
                  </tr>
                `
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        `;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError('Unable to open print window. Please allow pop-ups in your browser settings.');
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>${isPettyVoucher ? 'Petty Vouchers' : 'Petty Cash Vouchers'}</title>
            <style>
              .print-container {
                width: 100%;
                margin: 0;
                padding: 20px 20px 20px 0;
                font-family: Arial, sans-serif;
                background-color: #f8f9fa;
                min-height: 100vh;
              }
              
              .print-title {
                text-align: center;
                font-size: 24px;
                font-weight: bold;
                color: #333;
                margin-bottom: 20px;
                text-transform: uppercase;
                letter-spacing: 1px;
                background-color:#b39eb5;
              }
              
              .print-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
                margin-left: -5px;
                font-size: 12px;
                background-color: white;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              }
              
              .print-table th,
              .print-table td {
                border: 1px solid #333;
                padding: 8px;
                text-align: left;
                vertical-align: top;
              }
              
              .table-header {
                background-color: #b39eb5 !important;
                color: white !important;
              }
              
              .print-table th {
                background-color: #b39eb5;
                color: white;
                font-weight: bold;
                text-align: center;
              }
              
              .print-table tr:nth-child(even) {
                background-color: #f1f3f4;
              }
              
              .print-table tr:nth-child(odd) {
                background-color: white;
              }
              
              .print-table td {
                background-color: transparent;
                color: #333;
              }
            </style>
          </head>
          <body>
            ${printContent}
            <script>
              window.onload = function() {
                window.print();
                window.close();
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err) {
      setError(`Error during printing: ${err.message}`);
    }
  };

  const handleDownload = (isPettyVoucher) => {
    const vouchersToDownload = isPettyVoucher
      ? filterVouchersByDate(pettyVouchers, pettyVoucherFilterDate)
      : filterVouchersByDate(pettyCashVouchers, pettyCashVoucherFilterDate);

    if (!vouchersToDownload || vouchersToDownload.length === 0) {
      setError('No vouchers available to download');
      return;
    }

    try {
      const doc = new jsPDF();
      if (isPettyVoucher) {
        let yPos = 35; // Moved table down from 20 to 35
        doc.setFontSize(18);
        doc.setTextColor(30, 58, 138);
        doc.setFont(undefined, 'bold');
        // Center the text
        const pageWidth = doc.internal.pageSize.width;
        const textWidth = doc.getTextWidth('PETTY VOUCHERS');
        const centerXPos = (pageWidth - textWidth) / 2;
        doc.text('PETTY VOUCHERS', centerXPos, 15);

        doc.setFontSize(10);
        doc.setTextColor(0);

        const headers = [
          'Voucher ID',
          'Date',
          'Amount',
          'Purpose',
          'Paid To',
          'Account',
          'Received By',
          'Paid By',
        ];
        const colWidths = [20, 20, 30, 35, 25, 25, 25, 20]; // Adjusted widths for better spacing
        
        // Calculate total table width and center it
        const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);
        const pageWidth1 = doc.internal.pageSize.width;
        const leftMargin = (pageWidth1 - totalTableWidth) / 2;
        let currentX = leftMargin;
        const headerY = yPos;
        const headerHeight = 10;
        
        // Draw header background
        doc.setFillColor(240, 240, 240);
        doc.rect(currentX - 2, headerY - headerHeight, totalTableWidth + 4, headerHeight, 'F');
        
        // Draw header borders and text
        headers.forEach((header, i) => {
          // Draw cell border
          doc.setDrawColor(200, 200, 200);
          doc.rect(currentX - 2, headerY - headerHeight, colWidths[i], headerHeight);
          // Center text in cell
          const textX = currentX + (colWidths[i] / 2) - (doc.getTextWidth(header) / 2);
          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined, 'bold');
          doc.text(header, textX, headerY - 3);
          currentX += colWidths[i];
        });

        // Remove gap between header and data rows
        yPos += 10; // Reduced gap

        vouchersToDownload.forEach((v) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
            doc.setFontSize(18);
            doc.setTextColor(30, 58, 138);
            doc.setFont(undefined, 'bold');
            // Center the text
            const pageWidth = doc.internal.pageSize.width;
            const textWidth = doc.getTextWidth('PETTY VOUCHERS');
            const centerXPos = (pageWidth - textWidth) / 2;
            doc.text('PETTY VOUCHERS', centerXPos, 15);
            doc.setFontSize(10);
            doc.setTextColor(0);
            // Draw table header with borders
            const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);
            const pageWidth3 = doc.internal.pageSize.width;
            const leftMargin = (pageWidth3 - totalTableWidth) / 2;
            let currentX = leftMargin;
            const headerY = yPos;
            const headerHeight = 10;
            
            // Draw header background
            doc.setFillColor(240, 240, 240);
            doc.rect(currentX - 2, headerY - headerHeight, totalTableWidth + 4, headerHeight, 'F');
            
            headers.forEach((header, i) => {
              // Draw cell border
              doc.setDrawColor(200, 200, 200);
              doc.rect(currentX - 2, headerY - headerHeight, colWidths[i], headerHeight);
              // Center text in cell
              const textX = currentX + (colWidths[i] / 2) - (doc.getTextWidth(header) / 2);
              doc.setTextColor(0, 0, 0);
              doc.setFont(undefined, 'bold');
              doc.text(header, textX, headerY - 3);
              currentX += colWidths[i];
            });
            // Remove gap between header and data rows
            yPos += 10; // Reduced gap
          }

          // Draw data row with borders
          let dataX = leftMargin;
          const rowData = [
            v.voucherId || '-',
            v.date ? new Date(v.date).toLocaleDateString('en-GB') : currentDate,
            `₹${(v.amount || 0).toFixed(2)}`,
            v.purpose || '-',
            v.paidTo || '-',
            v.account || '-',
            v.receivedBy || '-',
            v.paidBy || '-'
          ];
          
          const rowHeight = 8;
          rowData.forEach((data, i) => {
            // Draw cell border
            doc.setDrawColor(200, 200, 200);
            doc.rect(dataX - 2, yPos - rowHeight, colWidths[i], rowHeight);
            
            // Position text based on column type
            let textX;
            if (i === 2) { // Amount column - right align
              textX = dataX + colWidths[i] - 5;
              doc.setTextColor(0, 0, 0);
              doc.setFont(undefined, 'normal');
              doc.text(data, textX, yPos - 3, { align: 'right' });
            } else { // Other columns - left align
              textX = dataX + 3;
              doc.setTextColor(0, 0, 0);
              doc.setFont(undefined, 'normal');
              doc.text(data, textX, yPos - 3);
            }
            dataX += colWidths[i];
          });
          yPos += 12; // Increased row spacing
        });
      } else {
        let yPos = 35; // Moved table down from 20 to 35
        doc.setFontSize(18);
        doc.setTextColor(30, 58, 138);
        doc.setFont(undefined, 'bold');
        // Center the text
        const pageWidth = doc.internal.pageSize.width;
        const textWidth = doc.getTextWidth('PETTY CASH VOCHERS');
        const centerXPos = (pageWidth - textWidth) / 2;
        doc.text('PETTY CASH VOCHERS', centerXPos, 15);

        doc.setFontSize(10);
        doc.setTextColor(0);

        const headers = [
          'Voucher ID',
          'Date',
          'Amount',
          'Purpose',
          'Paid To',
          'Account',
          'Received By',
          'Paid By',
          'Status',
          'Related Petty Voucher',
        ];
        const colWidths = [20, 20, 30, 35, 25, 25, 25, 20, 20, 30]; // Adjusted widths for better spacing
        
        // Calculate total table width and center it
        const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);
        const pageWidth2 = doc.internal.pageSize.width;
        const leftMargin = (pageWidth2 - totalTableWidth) / 2;
        let currentX = leftMargin;
        const headerY = yPos;
        const headerHeight = 10;
        
        // Draw header background
        doc.setFillColor(240, 240, 240);
        doc.rect(currentX - 2, headerY - headerHeight, totalTableWidth + 4, headerHeight, 'F');
        
        // Draw header borders and text
        headers.forEach((header, i) => {
          // Draw cell border
          doc.setDrawColor(200, 200, 200);
          doc.rect(currentX - 2, headerY - headerHeight, colWidths[i], headerHeight);
          // Center text in cell
          const textX = currentX + (colWidths[i] / 2) - (doc.getTextWidth(header) / 2);
          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined, 'bold');
          doc.text(header, textX, headerY - 3);
          currentX += colWidths[i];
        });

        // Remove gap between header and data rows
        yPos += 10; // Reduced gap

        vouchersToDownload.forEach((v) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
            doc.setFontSize(18);
            doc.setTextColor(30, 58, 138);
            doc.setFont(undefined, 'bold');
            // Center the text
            const pageWidth = doc.internal.pageSize.width;
            const textWidth = doc.getTextWidth('PETTY CASH VOCHERS');
            const centerXPos = (pageWidth - textWidth) / 2;
            doc.text('PETTY CASH VOCHERS', centerXPos, 15);
            doc.setFontSize(10);
            doc.setTextColor(0);
            // Draw table header with borders
            const totalTableWidth = colWidths.reduce((a, b) => a + b, 0);
            const pageWidth4 = doc.internal.pageSize.width;
            const leftMargin = (pageWidth4 - totalTableWidth) / 2;
            let currentX = leftMargin;
            const headerY = yPos;
            const headerHeight = 10;
            
            // Draw header background
            doc.setFillColor(240, 240, 240);
            doc.rect(currentX - 2, headerY - headerHeight, totalTableWidth + 4, headerHeight, 'F');
            
            headers.forEach((header, i) => {
              // Draw cell border
              doc.setDrawColor(200, 200, 200);
              doc.rect(currentX - 2, headerY - headerHeight, colWidths[i], headerHeight);
              // Center text in cell
              const textX = currentX + (colWidths[i] / 2) - (doc.getTextWidth(header) / 2);
              doc.setTextColor(0, 0, 0);
              doc.setFont(undefined, 'bold');
              doc.text(header, textX, headerY - 3);
              currentX += colWidths[i];
            });
            // Remove gap between header and data rows
            yPos += 10; // Reduced gap
          }

          // Draw data row with borders
          let dataX = leftMargin;
          const rowData = [
            v.voucherId || '-',
            v.date ? new Date(v.date).toLocaleDateString('en-GB') : currentDate,
            `₹${(v.amount || 0).toFixed(2)}`,
            v.purpose || '-',
            v.paidTo || '-',
            v.account || '-',
            v.receivedBy || '-',
            v.paidBy || '-',
            v.cashStatus || '-',
            v.relatedPettyVoucherId
              ? (() => {
                  const relatedVoucher = pettyVouchers.find((pv) => (pv._id === v.relatedPettyVoucherId || pv.id === v.relatedPettyVoucherId));
                  return relatedVoucher ? relatedVoucher.voucherId : '-';
                })()
              : '-'
          ];
          
          const rowHeight = 8;
          rowData.forEach((data, i) => {
            // Draw cell border
            doc.setDrawColor(200, 200, 200);
            doc.rect(dataX - 2, yPos - rowHeight, colWidths[i], rowHeight);
            
            // Position text based on column type
            let textX;
            if (i === 2) { // Amount column - right align
              textX = dataX + colWidths[i] - 5;
              doc.setTextColor(0, 0, 0);
              doc.setFont(undefined, 'normal');
              doc.text(data, textX, yPos - 3, { align: 'right' });
            } else { // Other columns - left align
              textX = dataX + 3;
              doc.setTextColor(0, 0, 0);
              doc.setFont(undefined, 'normal');
              doc.text(data, textX, yPos - 3);
            }
            dataX += colWidths[i];
          });
          yPos += 12; // Increased row spacing
        });
      }

      doc.save(`${isPettyVoucher ? 'petty-vouchers' : 'petty-cash-vouchers'}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      setError(`Error during download: ${err.message}`);
    }
  };

  const filterVouchersByDate = (vouchers, filterDate) => {
    if (!filterDate) return vouchers;
    try {
      const selectedDate = new Date(filterDate);
      return vouchers.filter((voucher) => {
        const voucherDate = new Date(voucher.date);
        return (
          voucherDate.getDate() === selectedDate.getDate() &&
          voucherDate.getMonth() === selectedDate.getMonth() &&
          voucherDate.getFullYear() === selectedDate.getFullYear()
        );
      });
    } catch (err) {
      setError('Invalid filter date');
      return vouchers;
    }
  };

  const filteredPettyVouchers = filterVouchersByDate(pettyVouchers, pettyVoucherFilterDate);
  const filteredPettyCashVouchers = filterVouchersByDate(pettyCashVouchers, pettyCashVoucherFilterDate);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  // Show loading spinner while authentication is being checked
  if (loading || !authInitialized || isRestoring) {
    return (
      <div className="digital-marketing-admin-panel">
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              {isRestoring ? 'Restoring session...' : 'Checking authentication...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="digital-marketing-admin-panel">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Digital Marketing Admin Panel</h2>
        <button
          onClick={handleSignOut}
          className="sign-out-button flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          disabled={isLoading}
        >
          <FaSignOutAlt />
          Sign Out
        </button>
      </div>
      {error && <p className="error-message">{error}</p>}
      {showSuccess && <p className="success-message">{showSuccess}</p>}

      <div className="mb-4 flex gap-4">
        <button
          onClick={() => setShowPettyVouchers(true)}
          className={`px-4 py-2 rounded toggle-button ${showPettyVouchers ? 'active' : ''}`}
          disabled={isLoading}
        >
          Petty Vouchers
        </button>
        <button
          onClick={() => setShowPettyVouchers(false)}
          className={`px-4 py-2 rounded toggle-button ${!showPettyVouchers ? 'active' : ''}`}
          disabled={isLoading}
        >
          Petty Cash Vouchers
        </button>
      </div>

      <div className={`book ${showPettyVouchers ? 'book--petty-vouchers' : 'book--petty-cash-vouchers'}`}>
        <div className="petty-vouchers-section">
          <h3 className="text-xl font-semibold mb-2">Petty Vouchers</h3>
          <div className="voucher-entry-box">
            <h4 className="text-lg mb-2">Create New Petty Voucher</h4>
            <div className="voucher-form">
              <input
                type="date"
                value={newVoucher.date}
                onChange={(e) => setNewVoucher({ ...newVoucher, date: e.target.value })}
                placeholder="Date"
                required
                disabled={isLoading}
              />
              <input
                type="number"
                value={newVoucher.amount}
                onChange={(e) => setNewVoucher({ ...newVoucher, amount: e.target.value })}
                placeholder="Amount"
                required
                step="0.01"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.purpose}
                onChange={(e) => setNewVoucher({ ...newVoucher, purpose: e.target.value })}
                placeholder="Purpose"
                required
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.paidTo}
                onChange={(e) => setNewVoucher({ ...newVoucher, paidTo: e.target.value })}
                placeholder="Paid To"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.account}
                onChange={(e) => setNewVoucher({ ...newVoucher, account: e.target.value })}
                placeholder="Account"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.receivedBy}
                onChange={(e) => setNewVoucher({ ...newVoucher, receivedBy: e.target.value })}
                placeholder="Received By"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.paidBy}
                onChange={(e) => setNewVoucher({ ...newVoucher, paidBy: e.target.value })}
                placeholder="Paid By"
                disabled={isLoading}
              />
              <button
                onClick={handleCreateVoucher}
                className="action-button px-4 py-2 rounded"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create Voucher'}
              </button>
            </div>
          </div>
          {editingVoucher && editingVoucher.isPettyVoucher ? (
            <div className="voucher-entry-box">
              <h4 className="text-lg mb-2">Edit Petty Voucher</h4>
              <div className="voucher-form">
                <input
                  type="date"
                  value={editingVoucher.date}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, date: e.target.value })}
                  required
                  disabled={isLoading}
                />
                <input
                  type="number"
                  value={editingVoucher.amount}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, amount: e.target.value })}
                  placeholder="Amount"
                  required
                  step="0.01"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.purpose}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, purpose: e.target.value })}
                  placeholder="Purpose"
                  required
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.paidTo}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, paidTo: e.target.value })}
                  placeholder="Paid To"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.account}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, account: e.target.value })}
                  placeholder="Account"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.receivedBy}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, receivedBy: e.target.value })}
                  placeholder="Received By"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.paidBy}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, paidBy: e.target.value })}
                  placeholder="Paid By"
                  disabled={isLoading}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveVoucher}
                    className="action-button px-4 py-2 rounded"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingVoucher(null)}
                    className="action-button px-4 py-2 rounded"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex gap-4 mb-4">
                <input
                  type="date"
                  value={pettyVoucherFilterDate}
                  onChange={(e) => setPettyVoucherFilterDate(e.target.value)}
                  className="border p-2 rounded"
                  disabled={isLoading}
                />
                <button
                  onClick={() => setPettyVoucherFilterDate('')}
                  className="action-button px-4 py-2 rounded"
                  disabled={isLoading}
                >
                  Clear Filter
                </button>
              </div>
              {filteredPettyVouchers.length > 0 ? (
                <table className="voucher-table">
                  <thead>
                    <tr>
                      <th>Voucher ID</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Purpose</th>
                      <th>Paid To</th>
                      <th>Account</th>
                      <th>Received By</th>
                      <th>Paid By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPettyVouchers.map((v, index) => (
                      <tr key={v._id || `petty-voucher-${index}`}>
                        <td>
                          {v.voucherId || '-'}
                          {v.isLocal && <span style={{color: '#28a745', marginLeft: '5px'}} title="Stored locally">📱</span>}
                        </td>
                        <td>{v.date ? new Date(v.date).toLocaleDateString('en-GB') : currentDate}</td>
                        <td>₹{(v.amount || 0).toFixed(2)}</td>
                        <td>{v.purpose || '-'}</td>
                        <td>{v.paidTo || '-'}</td>
                        <td>{v.account || '-'}</td>
                        <td>{v.receivedBy || '-'}</td>
                        <td>{v.paidBy || '-'}</td>
                        <td>
                          {isSuperAdmin ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditVoucher(v, true)}
                                className="action-button px-2 py-1 rounded"
                                disabled={isLoading}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteVoucher(v._id)}
                                className="action-button delete-button px-2 py-1 rounded"
                                disabled={isLoading}
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => sendEditRequest(v, 'pettyVoucher')}
                                className="action-button px-2 py-1 rounded bg-yellow-500 hover:bg-yellow-600"
                                disabled={isLoading}
                              >
                                Request Edit
                              </button>
                              <button
                                onClick={() => sendDeleteRequest(v, 'pettyVoucher')}
                                className="action-button px-2 py-1 rounded bg-red-500 hover:bg-red-600"
                                disabled={isLoading}
                              >
                                Request Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-vouchers-message">
                  <p className="text-gray-500 text-center py-8">
                    {pettyVoucherFilterDate 
                      ? `No petty vouchers found for the selected date.` 
                      : `No petty vouchers available. Create your first voucher above.`
                    }
                  </p>
                </div>
              )}
              <div className="flex gap-4 mt-4">
                <button
                  onClick={() => handlePrint(true)}
                  className="action-button px-4 py-2 rounded"
                  disabled={isLoading}
                >
                  Print
                </button>
                <button
                  onClick={() => handleDownload(true)}
                  className="action-button px-4 py-2 rounded"
                  disabled={isLoading}
                >
                  Download
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="petty-cash-vouchers-section">
          <h3 className="text-xl font-semibold mb-2">Petty Cash Vouchers</h3>
          <div className="voucher-entry-box">
            <h4 className="text-lg mb-2">Create New Voucher</h4>
            <div className="voucher-form">
              <select
                value={newVoucher.voucherType}
                onChange={(e) =>
                  setNewVoucher({ ...newVoucher, voucherType: e.target.value, relatedPettyVoucherId: '' })
                }
                disabled={isLoading}
              >
                <option value="pettyVoucher">Petty Voucher</option>
                <option value="pettyCashVoucher">Petty Cash Voucher</option>
              </select>
              {newVoucher.voucherType === 'pettyCashVoucher' && (
                <select
                  value={newVoucher.relatedPettyVoucherId}
                  onChange={(e) => setNewVoucher({ ...newVoucher, relatedPettyVoucherId: e.target.value })}
                  disabled={isLoading}
                >
                  <option value="">Select Related Petty Voucher (Optional)</option>
                  {pettyVouchers.map((v, index) => {
                    const voucherId = v._id || v.id;
                    return (
                      <option key={voucherId || `petty-voucher-option-${index}`} value={voucherId}>
                        {v.voucherId} - {v.purpose} (₹{v.amount.toFixed(2)})
                      </option>
                    );
                  })}
                </select>
              )}
              <input
                type="date"
                value={newVoucher.date}
                onChange={(e) => setNewVoucher({ ...newVoucher, date: e.target.value })}
                placeholder="Date"
                required
                disabled={isLoading}
              />
              <input
                type="number"
                value={newVoucher.amount}
                onChange={(e) => setNewVoucher({ ...newVoucher, amount: e.target.value })}
                placeholder="Amount"
                required
                step="0.01"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.purpose}
                onChange={(e) => setNewVoucher({ ...newVoucher, purpose: e.target.value })}
                placeholder="Purpose"
                required
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.paidTo}
                onChange={(e) => setNewVoucher({ ...newVoucher, paidTo: e.target.value })}
                placeholder="Paid To"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.account}
                onChange={(e) => setNewVoucher({ ...newVoucher, account: e.target.value })}
                placeholder="Account"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.receivedBy}
                onChange={(e) => setNewVoucher({ ...newVoucher, receivedBy: e.target.value })}
                placeholder="Received By"
                disabled={isLoading}
              />
              <input
                type="text"
                value={newVoucher.paidBy}
                onChange={(e) => setNewVoucher({ ...newVoucher, paidBy: e.target.value })}
                placeholder="Paid By"
                disabled={isLoading}
              />
              {newVoucher.voucherType === 'pettyCashVoucher' && (
                <select
                  value={newVoucher.cashStatus}
                  onChange={(e) => setNewVoucher({ ...newVoucher, cashStatus: e.target.value })}
                  disabled={isLoading}
                >
                  <option value="pending">Pending</option>
                  <option value="disbursed">Disbursed</option>
                  <option value="settled">Settled</option>
                </select>
              )}
              <button
                onClick={handleCreateVoucher}
                className="action-button px-4 py-2 rounded"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : newVoucher.voucherType === 'pettyVoucher' ? 'Create Petty Voucher' : 'Create Petty Cash Voucher'}
              </button>
            </div>
            {editingVoucher && !editingVoucher.isPettyVoucher ? (
              <div className="voucher-form edit-voucher-form mt-4">
                <h4 className="text-lg mb-2">Edit Petty Cash Voucher</h4>
                <select
                  value={editingVoucher.relatedPettyVoucherId}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, relatedPettyVoucherId: e.target.value })}
                  disabled={isLoading}
                >
                  <option value="">Select Related Petty Voucher (Optional)</option>
                  {pettyVouchers.map((v, index) => {
                    const voucherId = v._id || v.id;
                    return (
                      <option key={voucherId || `petty-voucher-option-${index}`} value={voucherId}>
                        {v.voucherId} - {v.purpose} (₹{v.amount.toFixed(2)})
                      </option>
                    );
                  })}
                </select>
                <input
                  type="date"
                  value={editingVoucher.date}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, date: e.target.value })}
                  required
                  disabled={isLoading}
                />
                <input
                  type="number"
                  value={editingVoucher.amount}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, amount: e.target.value })}
                  placeholder="Amount"
                  required
                  step="0.01"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.purpose}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, purpose: e.target.value })}
                  placeholder="Purpose"
                  required
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.paidTo}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, paidTo: e.target.value })}
                  placeholder="Paid To"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.account}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, account: e.target.value })}
                  placeholder="Account"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.receivedBy}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, receivedBy: e.target.value })}
                  placeholder="Received By"
                  disabled={isLoading}
                />
                <input
                  type="text"
                  value={editingVoucher.paidBy}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, paidBy: e.target.value })}
                  placeholder="Paid By"
                  disabled={isLoading}
                />
                <select
                  value={editingVoucher.cashStatus}
                  onChange={(e) => setEditingVoucher({ ...editingVoucher, cashStatus: e.target.value })}
                  disabled={isLoading}
                >
                  <option value="pending">Pending</option>
                  <option value="disbursed">Disbursed</option>
                  <option value="settled">Settled</option>
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveVoucher}
                    className="action-button px-4 py-2 rounded"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingVoucher(null)}
                    className="action-button px-4 py-2 rounded"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>

                </div>
              </div>
            ) : (
              <div>
                <div className="flex gap-4 mb-4">
                  <input
                    type="date"
                    value={pettyCashVoucherFilterDate}
                    onChange={(e) => setPettyCashVoucherFilterDate(e.target.value)}
                    className="border p-2 rounded"
                    disabled={isLoading}
                  />
                  <button
                    onClick={() => setPettyCashVoucherFilterDate('')}
                    className="action-button px-4 py-2 rounded"
                    disabled={isLoading}
                  >
                    Clear Filter
                  </button>
                </div>
                {filteredPettyCashVouchers.length > 0 ? (
                  <table className="voucher-table">
                    <thead>
                      <tr>
                        <th>Voucher ID</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Purpose</th>
                        <th>Paid To</th>
                        <th>Account</th>
                        <th>Received By</th>
                        <th>Paid By</th>
                        <th>Status</th>
                        <th>Related Petty Voucher</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPettyCashVouchers.map((v, index) => (
                        <tr key={v._id || `cash-voucher-${index}`}>
                          <td>
                            {v.voucherId || '-'}
                            {v.isLocal && <span style={{color: '#28a745', marginLeft: '5px'}} title="Stored locally">📱</span>}
                          </td>
                          <td>{v.date ? new Date(v.date).toLocaleDateString('en-GB') : currentDate}</td>
                          <td>₹{(v.amount || 0).toFixed(2)}</td>
                          <td>{v.purpose || '-'}</td>
                          <td>{v.paidTo || '-'}</td>
                          <td>{v.account || '-'}</td>
                          <td>{v.receivedBy || '-'}</td>
                          <td>{v.paidBy || '-'}</td>
                          <td>{v.cashStatus || '-'}</td>
                          <td>
                            {v.relatedPettyVoucherId
                              ? (() => {
                                  const relatedVoucher = pettyVouchers.find((pv) => pv._id === v.relatedPettyVoucherId || pv.id === v.relatedPettyVoucherId);
                                  return relatedVoucher ? (relatedVoucher.voucherId || relatedVoucher.id || 'Unknown') : 'Not Found';
                                })()
                              : '-'}
                          </td>
                          <td>
                            {isSuperAdmin ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditVoucher(v, false)}
                                  className="action-button edit-button px-2 py-1 rounded"
                                  disabled={isLoading}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteCashVoucher(v._id)}
                                  className="action-button delete-button px-2 py-1 rounded"
                                  disabled={isLoading}
                                >
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => sendEditRequest(v, 'pettyCashVoucher')}
                                  className="action-button px-2 py-1 rounded bg-yellow-500 hover:bg-yellow-600"
                                  disabled={isLoading}
                                >
                                  Request Edit
                                </button>
                                <button
                                  onClick={() => sendDeleteRequest(v, 'pettyCashVoucher')}
                                  className="action-button px-2 py-1 rounded bg-red-500 hover:bg-red-600"
                                  disabled={isLoading}
                                >
                                  Request Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-vouchers-message">
                    <p className="text-gray-500 text-center py-8">
                      {pettyCashVoucherFilterDate 
                        ? `No petty cash vouchers found for the selected date.` 
                        : `No petty cash vouchers available. Create your first voucher above.`
                      }
                    </p>
                  </div>
                )}
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={() => handlePrint(false)}
                    className="action-button px-4 py-2 rounded"
                    disabled={isLoading}
                  >
                    Print
                  </button>
                  <button
                    onClick={() => handleDownload(false)}
                    className="action-button px-4 py-2 rounded"
                    disabled={isLoading}
                  >
                    Download
                  </button>
                  <button className="back-btn" onClick={() => navigate(-1)}>Back</button>
                </div>
              </div>
            )}
            <button
              onClick={handleCreateVoucher}
              className="tear-button"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : newVoucher.voucherType === 'pettyVoucher' ? 'Submit & Tear (Petty Voucher)' : 'Submit & Tear (Petty Cash Voucher)'}
            </button>
          </div>
        </div>
      </div>

      {confettiActive && <Confetti width={width} height={height} />}
    </div>
  );
};

export default DigitalMarketingAdminPanel;